import {
  Autocomplete,
  Checkbox,
  Chip,
  FormControl,
  FormHelperText,
  FormLabel,
  InputAdornment,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { Controller } from "react-hook-form";
import { FormInputProps } from "../../models/form.model";
import { useConfig } from "../../containers/ConfigContext";
import { useMemo, useState } from "react";
import _ from "lodash";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  style: {
    zIndex: 1302,
  },
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

export interface SelectionOption {
  label: string | React.ReactNode;
  image?: string;
  value: string | number | boolean;
  disabled?: boolean;
}

export default function FormHierarchySelection(
  props: FormInputProps & {
    emptyText?: string;
    multiple?: boolean;
    displayType: "area" | "region" | "stores" | "all";
  }
) {
  const { name, control, emptyText, label, helperText, rules, required, multiple, setError, disabled } = props;
  const setErrorMsg = (message: string) => {
    setError(name, { type: "manual", message });
  };
  const { loggedUser, localized } = useConfig();
  const regionOptions: SelectionOption[] = [{ value: `${loggedUser?.companyId}`, label: "Company" }].concat(
    (loggedUser?.company?.region.split(",") || []).filter((a) => a).map((a) => ({ value: `${loggedUser?.companyId}_${a}`, label: a }))
  );
  const areaOptions: SelectionOption[] = (loggedUser?.company?.area.split(",") || [])
    .filter((a) => a)
    .map((a) => ({ value: `${loggedUser?.companyId}_${a}`, label: a }));
  const checkAll: any = { label: localized("Check All"), value: "check_all" };

  const storeOptions: SelectionOption[] = [checkAll].concat(
    (loggedUser?.company?.stores || []).map((a) => ({ value: a.id, label: a.name }))
  );
  const [storeInput, setStoreInput] = useState("");

  const storeInfoMap = useMemo(() => {
    return (loggedUser?.company?.stores || []).reduce((acc: any, store) => {
      acc[store.id] = { area: store.area, region: store.region };
      return acc;
    }, {});
  }, [loggedUser?.company?.stores]);

  const onFilterStoreOptions = (options: SelectionOption[], inputValue: string) => {
    options = options.filter((o: SelectionOption) => {
      const id = o.value.toString();
      const info = [o.label?.toString(), storeInfoMap[id]?.region, storeInfoMap[id]?.area];
      return o.value === checkAll.value || info.filter(Boolean).some((i) => i.toLowerCase().includes(inputValue.toLowerCase()));
    });
    return options;
  };

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field: { onChange, value: values = multiple ? [] : "" }, fieldState: { error }, formState }) => {
        if (multiple) {
          values = Array.isArray(values) ? values : [];
        } else {
          values = Array.isArray(values) ? "" : values;
        }
        return (
          <div>
            <FormLabel error={!!error} required={required} sx={{ mb: 2, display: "inline-block" }}>
              {label}
            </FormLabel>
            <Stack direction="row" spacing={2}>
              {(props.displayType === "all" || props.displayType === "region") && (
                <FormControl className="width-100p">
                  <InputLabel>Region</InputLabel>
                  <Select
                    multiple={multiple}
                    autoComplete="off"
                    value={values}
                    disabled={disabled}
                    onChange={(e) => {
                      const {
                        target: { value },
                      } = e;
                      onChange(multiple ? (typeof value === "string" ? value.split(",") : value) : value);
                    }}
                    input={<OutlinedInput label={"Region"} />}
                    renderValue={(selected) => {
                      return regionOptions
                        .filter((a) => (multiple ? ~selected.indexOf(`${a.value}`) : selected == a.value))
                        .map((v) => v.label)
                        .join(", ");
                    }}
                    MenuProps={MenuProps}
                  >
                    {regionOptions.map((op, i) => (
                      <MenuItem disabled={op.disabled} key={op.value + "_" + i} value={op.value.toString()}>
                        {multiple && <Checkbox checked={values && values.indexOf(op.value.toString()) > -1} />}
                        <ListItemText primary={op.label} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {(props.displayType === "all" || props.displayType === "area") && (
                <FormControl className="width-100p">
                  <InputLabel>Area</InputLabel>
                  <Select
                    multiple={multiple}
                    autoComplete="off"
                    value={values}
                    disabled={disabled}
                    onChange={(e) => {
                      const {
                        target: { value },
                      } = e;
                      onChange(multiple ? (typeof value === "string" ? value.split(",") : value) : value);
                    }}
                    input={<OutlinedInput label={"Area"} />}
                    renderValue={(selected) => {
                      return areaOptions
                        .filter((a) => (multiple ? ~selected.indexOf(`${a.value}`) : selected == a.value))
                        .map((v) => v.label)
                        .join(", ");
                    }}
                    MenuProps={MenuProps}
                  >
                    {areaOptions.map((op, i) => (
                      <MenuItem disabled={op.disabled} key={op.value + "_" + i} value={op.value.toString()}>
                        {multiple && <Checkbox checked={values && values.indexOf(op.value.toString()) > -1} />}
                        <ListItemText primary={op.label} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {(props.displayType === "all" || props.displayType === "stores") && (
                <FormControl className="width-100p">
                  <Autocomplete
                    sx={{ marginTop: -1 }}
                    multiple
                    disableCloseOnSelect
                    disabled={disabled}
                    options={storeOptions}
                    value={storeOptions.filter((o) => values.includes(o.value.toString()))}
                    onChange={(_, newValue) => {
                      const selectedOptions = newValue.map((option) => option.value.toString());
                      onChange(values.filter((v: string) => !v.startsWith("cpy_")).concat(selectedOptions));
                    }}
                    getOptionLabel={(option) => String(option.label || "")}
                    renderTags={(value) => <Chip label={value?.length} size="small" color="primary" />}
                    filterOptions={(options) => {
                      return onFilterStoreOptions(options, storeInput);
                    }}
                    inputValue={storeInput}
                    onInputChange={(event, newInputValue, reason) => {
                      if (reason === "input") {
                        setStoreInput(newInputValue);
                      }
                    }}
                    renderOption={(props, option) => {
                      return option.value === checkAll.value ? (
                        <li
                          {...props}
                          key={checkAll.value}
                          onClick={(e) => {
                            e.stopPropagation();
                            const allOptions = onFilterStoreOptions(storeOptions, storeInput)
                              .map((option) => option.value.toString())
                              .filter((v) => v !== checkAll.value);
                            onChange(_.uniq([...values, ...allOptions]));
                          }}
                        >
                          <Stack spacing={1} sx={{ flexGrow: 1, paddingTop: 1, paddingBottom: 1 }}>
                            <ListItemText primary={option.label} />
                          </Stack>
                        </li>
                      ) : (
                        <li {...props} key={`${option.value}-${option.label}`}>
                          <Checkbox
                            checked={Array.isArray(values) && values.includes(option.value.toString())}
                            style={{ marginRight: 8 }}
                          />
                          <Stack spacing={1} sx={{ flexGrow: 1 }}>
                            <ListItemText
                              primary={option.label}
                              primaryTypographyProps={{
                                sx: { whiteSpace: "normal", wordBreak: "break-word" },
                              }}
                            />
                            <ListItemText
                              secondary={`${storeInfoMap[option.value as string]?.region || ""} - ${
                                storeInfoMap[option.value as string]?.area || ""
                              }`}
                            />
                          </Stack>
                        </li>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        error={!!error}
                        inputProps={{
                          ...params.inputProps,
                          autoComplete: "new-password",
                          onKeyDown: (e) => {
                            e.stopPropagation();
                            if (e.key === "Backspace" && !storeInput) {
                              e.preventDefault();
                            }
                          },
                        }}
                        label="Store"
                      />
                    )}
                  />
                </FormControl>
              )}
            </Stack>
            <FormHelperText>{error ? error.message : helperText || null}</FormHelperText>
          </div>
        );
      }}
    />
  );
}
